"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Edit,
  Check,
  ArrowLeft,
  Package,
  IndianRupee,
  FileText,
  Trash,
} from "lucide-react";
import ImageComponent from "../../../../components/form-components/ImageComponent";
import {
  useFetchProductById,
  useUpdateProduct,
  useDeleteProduct,
} from "../../../../hooks/useFetchProduct";
import { Product } from "../../../../types/Product";
import LoadingComponent from "../../../../components/form-components/LoadingComponent";
import Spinner from "../../../Spinner";
import CustomDropdown from "../../../CustomDropdown";

type UpdatedFields = Partial<Omit<Product, "productImage">> & {
  productImage?: File;
  [key: string]: any;
};

const PRODUCT_TYPES = [
  { label: "Bajri", value: "bajri" },
  { label: "Bricks", value: "bricks" },
  { label: "Grit", value: "grit" },
  { label: "Cement", value: "cement" },
];
const VERIFIED_OPTIONS = [
  { value: true, label: "Verified" },
  { value: false, label: "Not Verified" },
];

const useClickOutside = (
  ref: React.RefObject<HTMLDivElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const ProductInfo: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { product, loading, error, fetchProduct } = useFetchProductById(
    productId as string,
  );
  const { updateProduct, loading: updating } = useUpdateProduct();
  const { deleteProduct, loading: deleting } = useDeleteProduct();

  const [isEditing, setIsEditing] = useState(false);
  const [productData, setProductData] = useState<Product | null>(null);
  const [updatedFields, setUpdatedFields] = useState<UpdatedFields>({});
  const [imageError, setImageError] = useState("");

  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isVerifiedDropdownOpen, setIsVerifiedDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const verifiedDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(typeDropdownRef, () => setIsTypeDropdownOpen(false));
  useClickOutside(verifiedDropdownRef, () => setIsVerifiedDropdownOpen(false));

  useEffect(() => {
    if (product) {
      setProductData(product);
    }
  }, [product]);

  const handleFieldChange = (field: keyof Product, value: any) => {
    setProductData((prev) => (prev ? { ...prev, [field]: value } : null));
    setUpdatedFields((prev) => ({ ...prev, [field]: value }));
  };

  const validateFile = useCallback((file: File) => {
    const validTypes = ["image/jpeg", "image/png", "image/svg+xml"];
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (!validTypes.includes(file.type)) {
      setImageError("Invalid file type. Please upload a JPEG, PNG, or SVG.");
      return false;
    }
    if (file.size > maxSize) {
      setImageError("File size exceeds 5 MB limit.");
      return false;
    }
    setImageError("");
    return true;
  }, []);

  const handleImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        if (validateFile(file)) {
          setUpdatedFields((prev) => ({ ...prev, productImage: file }));
        }
      }
    },
    [validateFile],
  );

  const handleUpdate = async () => {
    if (!productId || !productData || imageError) return;

    const formData = new FormData();
    Object.entries(updatedFields).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });

    try {
      const updatedProduct = await updateProduct(productId, formData);
      if (updatedProduct) {
        setProductData(updatedProduct);
        setIsEditing(false);
        setUpdatedFields({});
        fetchProduct();
      }
    } catch (err) {
      console.error("Failed to update product:", err);
    }
  };

  const handleDelete = async () => {
    if (!productId) return;
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        navigate("/products");
      } catch (err) {
        console.error("Failed to delete product:", err);
      }
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
  if (!productData) return <div className="p-6">No product data found</div>;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {productData.productType}
              </h1>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${productData.isVerified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
              >
                {productData.isVerified ? "Verified" : "Not Verified"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDelete}
              // disabled={deleting}
              disabled={deleting || !deleting}
              className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-colors disabled:text-gray-400 disabled:bg-transparent disabled:cursor-not-allowed"
            >
              <Trash size={20} />
            </button>
            {isEditing ? (
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:bg-gray-400 w-28 justify-center"
              >
                {updating ? <Spinner /> : <Check size={20} />}
                Save
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-300 transition-colors w-28 justify-center"
              >
                <Edit size={20} />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <InfoCard title="Product Details" icon={<Package />}>
              <DetailItem
                label="Product Size"
                value={productData.productSize}
                name="productSize"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("productSize", e.target.value)
                }
              />
              <DetailItem
                label="Location"
                value={productData.productLocation}
                name="productLocation"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("productLocation", e.target.value)
                }
              />
              <DetailItem
                label="Details"
                value={productData.productDetails}
                name="productDetails"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("productDetails", e.target.value)
                }
              />
            </InfoCard>
            <InfoCard title="Pricing & Stock" icon={<IndianRupee />}>
              <DetailItem
                label="Price"
                value={String(productData.productPrice)}
                name="productPrice"
                type="number"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("productPrice", parseFloat(e.target.value))
                }
              />
              <DetailItem
                label="Quantity"
                value={String(productData.productQuantity)}
                name="productQuantity"
                type="number"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange(
                    "productQuantity",
                    parseInt(e.target.value, 10),
                  )
                }
              />
              <DetailItem
                label="Rating"
                value={String(productData.productRating)}
                name="productRating"
                type="number"
                isEditing={isEditing}
                onChange={(e) =>
                  handleFieldChange("productRating", parseFloat(e.target.value))
                }
              />
            </InfoCard>
          </div>
          <div className="lg:col-span-1 space-y-6">
            <InfoCard title="Category & Status" icon={<Package />}>
              <CustomDropdown
                label="Product Type"
                value={productData.productType}
                options={PRODUCT_TYPES}
                isOpen={isTypeDropdownOpen}
                setIsOpen={setIsTypeDropdownOpen}
                onSelect={(value) => handleFieldChange("productType", value)}
                isEditing={isEditing}
                dropdownRef={typeDropdownRef}
              />
              <CustomDropdown
                label="Verified"
                value={productData.isVerified}
                options={VERIFIED_OPTIONS}
                isOpen={isVerifiedDropdownOpen}
                setIsOpen={setIsVerifiedDropdownOpen}
                onSelect={(value) => handleFieldChange("isVerified", value)}
                isEditing={isEditing}
                dropdownRef={verifiedDropdownRef}
              />
            </InfoCard>
            <InfoCard title="Product Image" icon={<FileText />}>
              <ImageComponent
                title=""
                imageUrl={productData.productImage || ""}
                onFileChange={isEditing ? handleImageChange : undefined}
                error={imageError}
                name="productImage"
              />
            </InfoCard>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center gap-3 mb-4">
      <div className="text-[#3c0b0b]">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const DetailItem: React.FC<{
  label: string;
  value: string;
  name: string;
  isEditing: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
}> = ({ label, value, name, isEditing, onChange, type = "text" }) => (
  <div>
    <label className="text-sm text-gray-500">{label}</label>
    {isEditing ? (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full p-2 border rounded-md mt-1"
      />
    ) : (
      <p className="font-medium text-gray-800">{value || "-"}</p>
    )}
  </div>
);

export default ProductInfo;
